import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlay, faUser } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import VoiceSelectModal from "@/pages/Private/pages/Courses/components/Editor/VoiceSelectModal";
import { AiModelUseCase, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import {
	DEFAULT_AUDIO_CONFIG,
	SubtitlePosition,
	SubtitleSize,
	SubtitleStyle,
	VideoWizard,
	WizardAudioConfig,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { SubtitlePicker } from "../components/SubtitlePicker";
import { ModelSelectRow } from "../ModelSelectRow";
import { isStepApproved } from "../wizard.utils";

interface Step4AudioProps {
	wizard: VideoWizard;
	isBusy: boolean;
	onPatchAudio: (audioConfig: WizardAudioConfig) => void;
	onSelectModel: (aiModelId: number) => void;
	onPreview: () => void;
	/** Speaks the approved script in the chosen voice — like the other steps' generate. */
	onGenerate: () => void;
	onApprove: () => void;
}

/**
 * Step 4 — the voice that speaks the approved words, and the subtitles under them.
 *
 * The text was settled in step 3; this step chooses the speaker, previews the sound, and
 * generates the spoken track. Changing the voice or the subtitles afterwards resets only
 * this step — the words survive it — so iterating here costs a take, never a script.
 */
export const Step4Audio: FunctionComponent<Step4AudioProps> = ({
	wizard,
	isBusy,
	onPatchAudio,
	onSelectModel,
	onPreview,
	onGenerate,
	onApprove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [isVoiceModalOpen, setVoiceModalOpen] = useState(false);

	const { scenes } = wizard.storyboard;
	// Chosen back in step 1. Derived once: the voice list filters on "de" while the label
	// is looked up by "DE", and spelling that fallback out at each use read like a constant.
	const languageCode = (wizard.narrationLanguage ?? "DE").toUpperCase();
	const languageName = ts(`languages.${languageCode}`);
	const approved = isStepApproved(wizard, 4);
	const audio = wizard.audioConfig ?? {};

	// Every default the server would apply, resolved once so each control below reads one
	// value rather than repeating the same fallback.
	const subtitleStyle = audio.subtitleStyle ?? DEFAULT_AUDIO_CONFIG.subtitleStyle;
	const subtitlePosition = audio.subtitlePosition ?? DEFAULT_AUDIO_CONFIG.subtitlePosition;
	const subtitleSize = audio.subtitleSize ?? DEFAULT_AUDIO_CONFIG.subtitleSize;

	// Only this step's own job spins here: the script step shares the job type, so the
	// step number on the job is what tells the two apart.
	const narrationJob = useMemo(
		() =>
			wizard.activeJobs.find((job) => job.type === MediaJobType.NARRATION_TTS && job.step !== 3),
		[wizard.activeJobs]
	);
	const previewJob = useMemo(
		() => wizard.activeJobs.find((job) => job.type === MediaJobType.NARRATION_PREVIEW),
		[wizard.activeJobs]
	);

	const isGenerating = !!narrationJob;
	const hasVoice = !!audio.voiceId;
	// The words are step 3's work; without them there is nothing to speak yet.
	const hasScript = !!scenes.length && scenes.every((scene) => !!scene.narrationText);
	// The track exists once every scene carries its narration audio — the server's own
	// approve precondition, read here so the buttons say what is actually possible.
	const hasNarration = !!scenes.length && scenes.every((scene) => !!scene.narrationAssetUrl);
	const spokenScenes = scenes.filter((scene) => !!scene.narrationAssetUrl);

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("steps.audio.heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">{ts("steps.audio.description")}</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: isGenerating
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("steps.audio.approvedBadge")
						: isGenerating
							? ts("steps.audio.generatingBadge")
							: ts("status.awaitingApproval")}
				</span>
			</header>

			<ModelSelectRow
				disabled={isBusy || isGenerating}
				step={4}
				useCase={AiModelUseCase.NARRATION_TTS}
				wizard={wizard}
				onSelectModel={onSelectModel}
			/>

			<section className="space-y-3 rounded-card border border-border-soft bg-white p-4">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<span className="block text-sm font-bold text-ink">
							{ts("steps.audio.languageLabel")}
						</span>
						<span className="mt-0.5 block text-xs text-subtle">{languageName}</span>
					</div>
					{/* The mockup's flag badge, kept for what it actually means: every voice in
					    the list speaks the chosen language, because the list is filtered to those
					    that do. It is not a statement about where data is processed. */}
					<span className="rounded-full bg-accent-tint px-2.5 py-1 text-xs font-semibold text-ink">
						{ts("steps.audio.languageSupported", { language: languageName })}
					</span>
				</div>

				<div className="border-t border-border-soft pt-3">
					<span className="block text-sm font-bold text-ink">{ts("steps.audio.voiceLabel")}</span>

					<div className="mt-2 flex flex-wrap items-center gap-3">
						<div
							className={classNames(
								"flex min-w-[220px] flex-1 items-center gap-2.5 rounded-card border px-3 py-2.5",
								hasVoice ? "border-accent bg-accent-tint" : "border-dashed border-border"
							)}
						>
							<Icon className="text-subtle" icon={faUser} />
							<span>
								<span className="block text-xs font-bold text-ink">
									{audio.voiceName || ts("steps.audio.noVoice")}
								</span>
								<span className="block text-xs text-subtle">
									{hasVoice ? ts("steps.audio.voiceChosen") : ts("steps.audio.noVoiceHint")}
								</span>
							</span>
						</div>

						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isBusy || isGenerating}
							size={ButtonSize.M}
							title={hasVoice ? ts("steps.audio.changeVoice") : ts("steps.audio.chooseVoice")}
							type="button"
							onClick={() => setVoiceModalOpen(true)}
						/>
					</div>
				</div>
			</section>

			<SubtitlePicker
				disabled={isBusy || isGenerating}
				position={subtitlePosition as SubtitlePosition}
				size={subtitleSize as SubtitleSize}
				style={subtitleStyle as SubtitleStyle}
				onChange={onPatchAudio}
			/>

			{!!spokenScenes.length && (
				<section className="space-y-2 rounded-card border border-border-soft bg-white p-4">
					<span className="block text-sm font-bold text-ink">{ts("steps.audio.trackHeading")}</span>
					<p className="text-xs text-subtle">{ts("steps.audio.trackHint")}</p>

					<div className="space-y-2">
						{spokenScenes.map((scene, index) => (
							<div key={scene.id} className="rounded-card border border-border-soft bg-surface p-3">
								<span className="text-xs font-bold text-ink">
									{ts("steps.audio.sceneNumber", { number: index + 1, title: scene.title })}
								</span>
								{!!scene.narrationText && (
									<p className="mt-1 text-xs text-subtle">{scene.narrationText}</p>
								)}
								<audio controls className="mt-2 w-full" src={scene.narrationAssetUrl ?? undefined}>
									<track kind="captions" />
								</audio>
							</div>
						))}
					</div>
				</section>
			)}

			<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span className="text-sm font-bold text-ink">{ts("steps.audio.iterateHeading")}</span>
				</div>

				{!!wizard.narrationPreviewUrl && (
					// Served from the aggregate rather than held in this component, so a reload
					// replays the sample instead of paying for a second one.
					<audio controls className="w-full" src={wizard.narrationPreviewUrl}>
						<track kind="captions" />
					</audio>
				)}

				<div className="flex flex-wrap gap-2">
					<Button
						className="flex-1"
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isBusy || isGenerating || !hasVoice || !!previewJob}
						image={<Icon className="mr-2" icon={faPlay} />}
						size={ButtonSize.M}
						title={
							previewJob
								? ts("steps.audio.previewRunning")
								: ts("steps.audio.preview", {
										voice: audio.voiceName ?? "",
									})
						}
						type="button"
						onClick={onPreview}
					/>
					<Button
						color={hasNarration ? ButtonColor.ACTION_SECONDARY : ButtonColor.ACTION}
						disabled={isBusy || isGenerating || !hasVoice || !hasScript}
						size={ButtonSize.M}
						title={
							isGenerating
								? ts("steps.audio.generating")
								: hasNarration
									? ts("steps.audio.regenerate")
									: ts("steps.audio.generate")
						}
						type="button"
						onClick={onGenerate}
					/>
					{hasNarration && !isGenerating && (
						<Button
							color={ButtonColor.ACTION}
							disabled={isBusy}
							size={ButtonSize.M}
							title={ts("steps.audio.approve")}
							type="button"
							onClick={onApprove}
						/>
					)}
				</div>

				{!hasVoice && <p className="text-xs text-subtle">{ts("steps.audio.approveBlockedHint")}</p>}
				{hasVoice && !hasScript && (
					<p className="text-xs text-subtle">{ts("steps.audio.noScriptHint")}</p>
				)}
				{hasVoice && hasScript && !hasNarration && !isGenerating && (
					<p className="text-xs text-subtle">{ts("steps.audio.generateHint")}</p>
				)}
				{isGenerating && <p className="text-xs text-subtle">{ts("steps.audio.generatingHint")}</p>}
			</section>

			<VoiceSelectModal
				courseLanguage={languageCode.toLowerCase()}
				isOpen={isVoiceModalOpen}
				selectedVoiceId={audio.voiceId ?? ""}
				onClose={() => setVoiceModalOpen(false)}
				onConfirm={(voiceId, voiceName) => {
					onPatchAudio({ voiceId, voiceName });
					setVoiceModalOpen(false);
				}}
			/>
		</div>
	);
};
